import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getProducts, submitOrderRequest } from '../utils/api';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import { useAuth } from '../context/AuthContext';

// ── Michelin catalog data ──────────────────────────────────────────────────
const TIRE_IMAGES = [
  'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=400&q=80',
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80',
  'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=400&q=80',
  'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=400&q=80',
  'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=400&q=80',
  'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&q=80',
];

// eslint-disable-next-line no-unused-vars
const michelinCatalog = {
  16: [
    { size: '205/55R16', rating: '91H', mspn: '35861', tags: [] },
    { size: '205/60R16', rating: '92V', mspn: '61910', tags: [] },
    { size: '205/65R16', rating: '95H', mspn: '76709', tags: [] },
    { size: '215/55R16', rating: '97H', mspn: '03995', tags: ['XL'] },
    { size: '215/60R16', rating: '95V', mspn: '31690', tags: [] },
  ],
  17: [
    { size: '205/50R17', rating: '93V', mspn: '93347', tags: ['XL'] },
    { size: '205/55R17', rating: '95V', mspn: '21343', tags: ['XL'] },
    { size: '215/45R17', rating: '91V', mspn: '17814', tags: ['XL'] },
    { size: '215/50R17', rating: '95V', mspn: '17193', tags: ['XL'] },
    { size: '215/55R17', rating: '94V', mspn: '46271', tags: [] },
    { size: '225/45R17', rating: '91V', mspn: '54271', tags: [] },
    { size: '225/50R17', rating: '98V', mspn: '69757', tags: ['XL'] },
    { size: '245/45ZR17', rating: '(99Y)', mspn: '73361', tags: ['XL'] },
  ],
  18: [
    { size: '225/40R18', rating: '92V', mspn: '52883', tags: ['XL'] },
    { size: '225/45R18', rating: '95W', mspn: '87994', tags: ['XL', 'MB'] },
    { size: '225/50R18', rating: '95H', mspn: '29568', tags: [] },
    { size: '235/40R18', rating: '95V', mspn: '45388', tags: ['XL'] },
    { size: '235/45R18', rating: '98V', mspn: '16172', tags: ['XL'] },
    { size: '245/40R18', rating: '97V', mspn: '34063', tags: ['XL'] },
    { size: '255/35R18', rating: '94V', mspn: '04559', tags: ['XL'] },
    { size: '265/60R18', rating: '110V', mspn: '46728', tags: [] },
  ],
  19: [
    { size: '225/40R19', rating: '93Y', mspn: '58077', tags: ['XL', 'BMW'] },
    { size: '235/40R19', rating: '96V', mspn: '60820', tags: ['XL'] },
    { size: '245/45R19', rating: '102V', mspn: '38373', tags: ['XL'] },
    { size: '255/35R19', rating: '96Y', mspn: '96987', tags: ['XL', 'BMW'] },
    { size: '255/45R19', rating: '104V', mspn: '23187', tags: ['XL'] },
    { size: '265/50R19', rating: '110V', mspn: '70977', tags: ['XL'] },
  ],
  20: [
    { size: '235/45R20', rating: '100H', mspn: '10503', tags: ['XL'] },
    { size: '245/40R20', rating: '99W', mspn: '52079', tags: ['XL'] },
    { size: '255/40R20', rating: '101W', mspn: '61274', tags: ['XL'] },
    { size: '255/45R20', rating: '105V', mspn: '30754', tags: ['XL'] },
    { size: '265/45R20', rating: '108V', mspn: '08932', tags: ['XL'] },
  ],
  21: [
    { size: '245/45R21', rating: '104V', mspn: '31007', tags: ['XL'] },
    { size: '255/40R21', rating: '102V', mspn: '03271', tags: ['XL'] },
    { size: '275/40R21', rating: '107V', mspn: '33925', tags: ['XL'] },
    { size: '285/45R21', rating: '113V', mspn: '69136', tags: ['XL'] },
  ],
  22: [
    { size: '255/45R22', rating: '107V', mspn: '00671', tags: ['XL'] },
    { size: '275/40R22', rating: '108V', mspn: '59847', tags: ['XL'] },
    { size: '285/40R22', rating: '110W', mspn: '91725', tags: ['XL'] },
    { size: '285/45R22', rating: '114H', mspn: '05254', tags: ['XL'] },
  ],
};

// ── Order Modal ────────────────────────────────────────────────────────────
const OrderModal = ({ product, michelinTire, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    quantity: 1,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const title = product
    ? `${product.brand} ${product.name} — ${product.size}`
    : `Michelin® ${michelinTire?.size} ${michelinTire?.rating}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitOrderRequest({
        customerName: form.name,
        phone: form.phone,
        email: form.email,
        address: user?.address || 'To be confirmed',
        city: 'To be confirmed',
        subtotal: product ? product.price * form.quantity : 0,
        message: michelinTire
          ? `MSPN: ${michelinTire.mspn} | Size: ${michelinTire.size} | ${form.message}`
          : form.message,
        items: [{
          name: product ? product.name : `Michelin® ${michelinTire?.size}`,
          brand: product ? product.brand : 'Michelin',
          size: product ? product.size : michelinTire?.size,
          quantity: Number(form.quantity),
          price: product ? product.price : 0,
        }]
      });
      setDone(true);
    } catch {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={ms.overlay} onClick={onClose}>
      <div style={ms.box} onClick={e => e.stopPropagation()}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: '48px', margin: '0 0 12px' }}>✅</p>
            <h3 style={{ color: '#1a3a5c', margin: '0 0 8px' }}>Order Request Sent!</h3>
            <p style={{ color: '#4a5568', fontSize: '14px', margin: '0 0 20px' }}>Our team will contact you shortly.</p>
            <button style={ms.closeBtn} onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <p style={{ color: '#c41e3a', fontSize: '11px', fontWeight: 'bold', letterSpacing: '2px', margin: '0 0 4px' }}>ORDER REQUEST</p>
                <h3 style={{ color: '#1a3a5c', margin: 0, fontSize: '16px' }}>{title}</h3>
                {michelinTire && <p style={{ color: '#4a5568', fontSize: '12px', margin: '4px 0 0' }}>MSPN #{michelinTire.mspn}</p>}
              </div>
              <button style={ms.xBtn} onClick={onClose}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input style={ms.input} placeholder="Your Full Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input style={ms.input} placeholder="Phone Number *" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <input style={ms.input} placeholder="Email (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ color: '#4a5568', fontSize: '13px', whiteSpace: 'nowrap' }}>Quantity:</label>
                <input style={{ ...ms.input, width: '80px' }} type="number" min="1" max="20" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <textarea style={{ ...ms.input, height: '68px', resize: 'vertical' }} placeholder="Notes or questions..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
              <button type="submit" style={{ ...ms.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
                {submitting ? 'Sending...' : '📤 Send Order Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const ms = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#fff', borderRadius: '18px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  xBtn: { background: '#f5f7fa', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', fontSize: '15px', color: '#4a5568', flexShrink: 0 },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '14px', color: '#1a3a5c', outline: 'none', background: '#f9fafc', boxSizing: 'border-box' },
  submitBtn: { padding: '12px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' },
  closeBtn: { padding: '10px 28px', background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontWeight: '600' },
};

// ── Main Page ──────────────────────────────────────────────────────────────
const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [adminProducts, setAdminProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('size'); // 'size' | 'mspn'
  const [selectedRim, setSelectedRim] = useState(null); // null = all
  const [orderTarget, setOrderTarget] = useState(null); // product object

  const rimSizes = [16, 17, 18, 19, 20, 21, 22];

  // Read URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mspn')) { setSearchType('mspn'); setSearchQuery(params.get('mspn')); }
    if (params.get('size')) { setSearchType('size'); setSearchQuery(params.get('size')); }
  }, [location.search]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const { data } = await getProducts({ limit: 500 });
        setAdminProducts(data.products || []);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ── Filter logic ──
  const q = searchQuery.trim().toLowerCase();

  const filteredProducts = adminProducts.filter(p => {
    if (!q && selectedRim === null) return true;
    const sizeMatch = p.size?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
    const mspnMatch = (p.mspn && p.mspn.toLowerCase().includes(q)) || p.modelName?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
    const rimMatch = selectedRim ? (p.size?.includes(`R${selectedRim}`) || p.category === `R${selectedRim}`) : true;
    if (q) return (searchType === 'size' ? sizeMatch : mspnMatch) && rimMatch;
    return rimMatch;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/products?${searchType}=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div style={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <Navbar />
      {orderTarget && (
        <OrderModal
          product={orderTarget}
          onClose={() => setOrderTarget(null)}
        />
      )}

      <div style={styles.container}>
        <h1 style={styles.title}>All Products</h1>
        <p style={styles.subtitle}>Browse our full tire catalog — select a rim size or search by size / MSPN</p>

        {/* ── SEARCH BAR ── */}
        <div style={styles.searchCard}>
          <div style={styles.tabRow}>
            <button style={{ ...styles.tab, ...(searchType === 'size' ? styles.tabActive : {}) }} onClick={() => setSearchType('size')}>
              📏 Search by Tire Size
            </button>
            <button style={{ ...styles.tab, ...(searchType === 'mspn' ? styles.tabActive : {}) }} onClick={() => setSearchType('mspn')}>
              🔢 Search by MSPN Number
            </button>
          </div>
          <form onSubmit={handleSearch} style={styles.searchRow}>
            <input
              style={styles.searchInput}
              placeholder={searchType === 'size' ? 'e.g. 225/55R17, 255/40R20 …' : 'e.g. 35861, 93347 …'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <button type="submit" style={styles.searchBtn}>Search →</button>
            {(searchQuery || selectedRim) && (
              <button type="button" style={styles.clearBtn} onClick={() => { setSearchQuery(''); setSelectedRim(null); navigate('/products'); }}>
                ✕ Clear
              </button>
            )}
          </form>
        </div>

        {/* ── RIM SIZE PILLS ── */}
        <div style={styles.rimRow}>
          <button style={{ ...styles.rimPill, ...(selectedRim === null ? styles.rimPillActive : {}) }} onClick={() => setSelectedRim(null)}>
            All Sizes
          </button>
          {rimSizes.map(r => (
            <button key={r}
              style={{ ...styles.rimPill, ...(selectedRim === r ? styles.rimPillActive : {}) }}
              onClick={() => setSelectedRim(r)}
            >R{r}</button>
          ))}
        </div>

        {/* ── RESULTS SUMMARY ── */}
        <p style={styles.resultText}>
          Showing <strong>{filteredProducts.length}</strong> products
          {searchQuery && <> for "<strong>{searchQuery}</strong>"</>}
          {selectedRim && <> in <strong>R{selectedRim}</strong></>}
        </p>

        {/* ── UNIFIED PRODUCT GRID ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#8a9bae' }}>Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div style={styles.emptyBox}>
            <p style={{ fontSize: '48px', margin: '0 0 12px' }}>🔍</p>
            <p style={{ color: '#1a3a5c', fontWeight: 'bold', fontSize: '18px' }}>No results found</p>
            <p style={{ color: '#4a5568', fontSize: '14px' }}>Try a different size or MSPN number</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {filteredProducts.map((p, i) => (
              <div key={p._id} style={styles.card}>
                <div style={styles.imgBox}>
                  {p.discountPercent > 0 && <span style={styles.saleBadge}>{p.discountPercent}% OFF</span>}
                  {p.mspn && <span style={styles.mspnBadge}>#{p.mspn}</span>}
                  <span style={styles.typeBadge}>{p.type === 'tyre' ? 'Tire' : 'Rim'}</span>
                  <img
                    src={p.images?.[0] || TIRE_IMAGES[i % TIRE_IMAGES.length]}
                    alt={p.name}
                    style={styles.img}
                  />
                </div>
                <div style={styles.cardBody}>
                  <p style={styles.brandLabel}>{p.brand}</p>
                  <h3 style={styles.productName}>{p.name}</h3>
                  <p style={styles.sizeLabel}>{p.size} {p.speedRating ? `(${p.speedRating})` : ''}</p>
                  <div style={styles.priceRow}>
                    {p.discountPercent > 0 ? (
                      <>
                        <span style={styles.priceMain}>$ {(p.price * (1 - p.discountPercent / 100)).toLocaleString()}</span>
                        <span style={styles.priceOld}>$ {p.price?.toLocaleString()}</span>
                      </>
                    ) : (
                      <span style={styles.priceMain}>$ {p.price?.toLocaleString()}</span>
                    )}
                  </div>
                  <button style={styles.orderBtn} onClick={() => setOrderTarget(p)}>Order Now →</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' },
  title: { color: '#1a3a5c', fontSize: '32px', fontWeight: 'bold', marginBottom: '8px', textAlign: 'center' },
  subtitle: { color: '#4a5568', fontSize: '15px', textAlign: 'center', marginBottom: '28px' },

  searchCard: { background: '#fff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(26,58,92,0.06)', marginBottom: '20px' },
  tabRow: { display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' },
  tab: { flex: 1, padding: '10px 14px', background: '#f5f7fa', border: '2px solid #e2e8f0', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#4a5568', minWidth: '150px' },
  tabActive: { background: '#1a3a5c', borderColor: '#1a3a5c', color: '#fff' },
  searchRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  searchInput: { flex: 1, padding: '11px 16px', border: '2px solid #e2e8f0', borderRadius: '9px', fontSize: '14px', color: '#1a3a5c', outline: 'none', minWidth: '200px' },
  searchBtn: { padding: '11px 24px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '9px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },
  clearBtn: { padding: '11px 16px', background: '#f5f7fa', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

  rimRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  rimPill: { padding: '8px 18px', background: '#fff', border: '2px solid #e2e8f0', borderRadius: '50px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#1a3a5c' },
  rimPillActive: { background: '#1a3a5c', borderColor: '#1a3a5c', color: '#fff' },

  resultText: { color: '#4a5568', fontSize: '13px', marginBottom: '20px' },
  emptyBox: { textAlign: 'center', padding: '80px 20px' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '16px' },
  card: { background: '#fff', borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(26,58,92,0.05)', display: 'flex', flexDirection: 'column' },
  imgBox: { height: '160px', background: '#eef2f7', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  imgPlaceholder: { fontSize: '48px', opacity: 0.3 },
  saleBadge: { position: 'absolute', top: '8px', left: '8px', background: '#c41e3a', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', zIndex: 1 },
  typeBadge: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.92)', color: '#4a5568', fontSize: '9px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', textTransform: 'uppercase', zIndex: 1 },
  mspnBadge: { position: 'absolute', bottom: '8px', left: '8px', background: '#c41e3a', color: '#fff', fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', zIndex: 1 },
  cardBody: { padding: '14px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  brandLabel: { color: '#c41e3a', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', margin: 0 },
  productName: { color: '#1a3a5c', fontSize: '14px', fontWeight: '700', margin: 0 },
  sizeLabel: { color: '#4a5568', fontSize: '12px', margin: 0 },
  priceRow: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' },
  priceMain: { color: '#c41e3a', fontSize: '16px', fontWeight: 'bold' },
  priceOld: { color: '#bbb', fontSize: '12px', textDecoration: 'line-through' },
  tag: { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: '#e8f4fd', color: '#2d7dd2', textTransform: 'uppercase' },
  orderBtn: { marginTop: 'auto', padding: '9px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
};

export default ProductsPage;